import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react'
import { Map } from 'immutable'
import { useDispatch } from 'react-redux'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import { H2 } from '../Heading/index'
import Skeleton from '../Skeleton/index'
import ThumbsUp from '../Icon/icons/ThumbsUp/index'
import ThumbsDown from '../Icon/icons/ThumbsDown/index'
import Voice from '../Icon/icons/Voice/index'
import { parseRefinementTags } from '../../services/community/oracle/discover-utils'
import { usePostHogEvent } from '../PostHog/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import {
  AI_RESPONSE_FEEDBACK_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_MESSAGE_RECEIVED_GA4_EVENT,
  AI_MODULE_CLICKED_GA4_EVENT,
} from '../../services/event-tracking/index'

interface AiGuideProps {
  staticText: Map<string, unknown>
  streamingText: string
  loading: boolean
  isStreaming: boolean
  statusKey: string | null
  error: string | null
  refine: (text: string) => void
  sendFeedback: (thumbsValue: 'up' | 'down' | 'none') => Promise<void>
  turnId: string | null
}

function AiGuide ({
  staticText,
  streamingText,
  loading,
  isStreaming,
  statusKey,
  error,
  refine,
  sendFeedback,
  turnId,
}: AiGuideProps): React.ReactElement {
  const [refinementInput, setRefinementInput] = useState('')
  const [thumbsValue, setThumbsValue] = useState<'up' | 'down' | null>(null)

  const dispatch = useDispatch()

  // ── PostHog + GA4 event tracking ────────────────────────────────
  const trackMessageFeedback = usePostHogEvent('ai_message_feedback')
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const trackModuleClicked = usePostHogEvent('ai_module_clicked')
  const trackMessageReceived = usePostHogEvent('ai_message_received')

  // Reset thumbs state when turnId changes (new search/refinement)
  useEffect(() => {
    setThumbsValue(null)
  }, [turnId])

  // ── Static text ─────────────────────────────────────────────────
  const aiGuideLabel = staticText.getIn(['data', 'aiGuide'], 'Search') as string
  const typeToRefineLabel = staticText.getIn(
    ['data', 'typeToRefine'],
    'Type to refine...',
  ) as string
  const tryAgainLabel = staticText.getIn(
    ['data', 'tryAgain'],
    'Try Again',
  ) as string
  const aiGuideErrorLabel = staticText.getIn(
    ['data', 'aiGuideError'],
    'Something went wrong. Please try again.',
  ) as string
  const submitRefinementLabel = staticText.getIn(
    ['data', 'submitRefinement'],
    'Submit refinement',
  ) as string
  const helpfulLabel = (staticText.getIn(['data', 'discoverHelpful']) as string) || 'Helpful'
  const notHelpfulLabel = (staticText.getIn(['data', 'discoverNotHelpful']) as string) || 'Not helpful'
  const statusSearchingLabel = staticText.getIn(
    ['data', 'statusSearching'],
    'Searching across our library...',
  ) as string
  const statusGeneratingLabel = staticText.getIn(
    ['data', 'statusGenerating'],
    'Putting together your results...',
  ) as string

  // ── Feedback handler ──────────────────────────────────────────
  const handleThumbsClick = useCallback(async (value: 'up' | 'down') => {
    if (thumbsValue === value) {
      setThumbsValue(null)
      await sendFeedback('none')
    } else {
      setThumbsValue(value)
      await sendFeedback(value)
      trackMessageFeedback(value === 'up' ? 'Up' : 'Down')
      dispatch(setAiGa4Event(AI_RESPONSE_FEEDBACK_GA4_EVENT))
    }
  }, [thumbsValue, sendFeedback, trackMessageFeedback, dispatch])

  // ── Refinement handlers ─────────────────────────────────────────
  const handleRefineSubmit = useCallback(() => {
    if (refinementInput.trim()) {
      refine(refinementInput.trim())
      trackMessageSent(refinementInput.trim(), 'Text', 'Text Field', 'keyboard')
      dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT))
      setRefinementInput('')
    }
  }, [refine, refinementInput, trackMessageSent, dispatch])

  const handleRefineKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleRefineSubmit()
    },
    [handleRefineSubmit],
  )

  // ── Narrative ───────────────────────────────────────────────────
  const parsedSegments = useMemo(
    () => parseRefinementTags(streamingText || ''),
    [streamingText],
  )

  // ── Status text resolution ─────────────────────────────────────
  const statusLabels: Record<string, string> = {
    searching: statusSearchingLabel,
    generating: statusGeneratingLabel,
  }
  const statusLabel = statusKey ? statusLabels[statusKey] || null : null

  // ── State helpers ───────────────────────────────────────────────
  // Skeleton renders inside `__ai-text` whenever the page is loading and
  // no narrative has streamed yet. Shares the same load condition as
  // `__status-text` (which still pulses above the skeleton when a
  // `statusKey` is set) — both indicators coexist during loading.
  const showSkeleton = loading && !streamingText && !error
  const showFeedbackButtons = (
    !!streamingText && !!turnId && !loading && !isStreaming && !error
  )

  // Track when overview stream completes
  useEffect(() => {
    if (showFeedbackButtons && turnId && streamingText) {
      trackMessageReceived(turnId, streamingText.length, 'Text')
      dispatch(setAiGa4Event(AI_MESSAGE_RECEIVED_GA4_EVENT))
    }
  }, [showFeedbackButtons, turnId, streamingText, trackMessageReceived, dispatch])

  return (
    <div className="ai-guide-search__ai-guide">
      <H2 className="ai-guide-search__ai-heading">
        {aiGuideLabel}
      </H2>

      {/* Slot 1: `__ai-text` — always rendered so the parent flex
          layout doesn't reflow between states. Inner content varies:
          error message, status label, narrative, skeleton lines, or
          empty (no-query state). */}
      <div className="ai-guide-search__ai-text">
        {error && (
          <p className="ai-guide-search__ai-error">
            {aiGuideErrorLabel}
            {' '}
            <Button
              type={BUTTON_TYPES.LINK}
              className="ai-guide-search__ai-link"
              onClick={() => refine('')}
            >
              {tryAgainLabel}
            </Button>
          </p>
        )}
        {!error && !streamingText && loading && (
          <p className="ai-guide-search__status-text">
            {statusLabel || ' '}
          </p>
        )}
        {!error && streamingText && (
          <p>
            {parsedSegments.map((segment, i) => {
              const key = `seg-${i}`
              if (segment.type === 'refine') {
                return (
                  <a
                    key={key}
                    href="#"
                    role="button"
                    className="ai-guide-search__refine-chip ai-guide-search__refine-chip--inline"
                    onClick={(e) => {
                      e.preventDefault()
                      refine(segment.value)
                      trackModuleClicked('refine_chip')
                      dispatch(setAiGa4Event(AI_MODULE_CLICKED_GA4_EVENT))
                    }}
                  >
                    {segment.value}
                  </a>
                )
              }
              return <span key={key}>{segment.value}</span>
            })}
            {isStreaming && (
              <span className="ai-guide-search__streaming-cursor" aria-hidden="true" />
            )}
          </p>
        )}
        {showSkeleton && (
          <div className="ai-guide-search__ai-text-skeleton">
            <Skeleton height={15} />
            <Skeleton height={15} />
            <Skeleton height={15} />
            <Skeleton width="60%" height={15} />
          </div>
        )}
      </div>

      {/* Slot 2: `__ai-feedback` — only renders once the narrative
          has fully streamed in. `margin-top: auto` in SCSS pins this
          row + the input wrapper that follows it to the bottom of
          `__ai-guide`'s flex column. */}
      {showFeedbackButtons && (
        <div className="ai-guide-search__ai-feedback">
          <button
            type="button"
            className={`ai-guide-search__ai-feedback-btn${thumbsValue === 'up' ? ' ai-guide-search__ai-feedback-btn--active' : ''}`}
            onClick={() => handleThumbsClick('up')}
            aria-label={helpfulLabel}
            aria-pressed={thumbsValue === 'up'}
          >
            <ThumbsUp size="16px" filled={thumbsValue === 'up'} />
            <span>{helpfulLabel}</span>
          </button>
          <button
            type="button"
            className={`ai-guide-search__ai-feedback-btn${thumbsValue === 'down' ? ' ai-guide-search__ai-feedback-btn--active' : ''}`}
            onClick={() => handleThumbsClick('down')}
            aria-label={notHelpfulLabel}
            aria-pressed={thumbsValue === 'down'}
          >
            <ThumbsDown size="16px" filled={thumbsValue === 'down'} />
            <span>{notHelpfulLabel}</span>
          </button>
        </div>
      )}

      {/* Slot 3: `__ai-input-wrapper` — appears alongside `__ai-feedback`
          once the narrative has fully streamed in. Pinned to the
          section bottom via the feedback row's `margin-top: auto`. */}
      {showFeedbackButtons && (
        <div className="ai-guide-search__ai-input-wrapper">
          <input
            className="ai-guide-search__ai-input"
            type="text"
            placeholder={typeToRefineLabel}
            value={refinementInput}
            onChange={(e) => setRefinementInput(e.target.value)}
            onKeyDown={handleRefineKeyDown}
          />
          <Button
            type={BUTTON_TYPES.ICON}
            iconSvg={<Voice size="24px" color="#fff" />}
            className="ai-guide-search__ai-voice-btn"
            aria-label={submitRefinementLabel}
            onClick={handleRefineSubmit}
          />
        </div>
      )}
    </div>
  )
}

export default AiGuide
